import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { OrgProvider } from "@/context/OrgContext";
import { applyStoredTheme } from "@/lib/theme";
import ProtectedRoute from "@/components/ProtectedRoute";
import RequireAuth from "@/components/RequireAuth";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Registry from "@/pages/Registry";
import Members from "@/pages/Members";
import ApiKeys from "@/pages/ApiKeys";
import Gdpr from "@/pages/Gdpr";
import Knowledge from "@/pages/Knowledge";
import KnowledgeItemEditor from "@/pages/KnowledgeItemEditor";
import Events from "@/pages/Events";
import Governance from "@/pages/Governance";
import Media from "@/pages/Media";
import BI from "@/pages/BI";
import Integrations from "@/pages/Integrations";
import AIStudio from "@/pages/AIStudio";
import Social from "@/pages/Social";
import Ecommerce from "@/pages/Ecommerce";
import EmailMarketing from "@/pages/EmailMarketing";
import Analytics from "@/pages/Analytics";
import Workflows from "@/pages/Workflows";
import ProductHunter from "@/pages/ProductHunter";
import PromotionLayout from "@/promotions/PromotionLayout";
import PromotionHome from "@/promotions/pages/PromotionHome";
import ConnectionHealthPage from "@/promotions/pages/ConnectionHealthPage";
import NewCampaign from "@/promotions/pages/NewCampaign";
import Campaigns from "@/promotions/pages/Campaigns";
import CampaignDetail from "@/promotions/pages/CampaignDetail";
import ProductCatalog from "@/promotions/pages/ProductCatalog";
import Queue from "@/promotions/pages/Queue";
import Timeline from "@/promotions/pages/Timeline";
import PreviewCenter from "@/promotions/pages/PreviewCenter";
import PerformanceAnalytics from "@/promotions/pages/PerformanceAnalytics";
import AITemplates from "@/promotions/pages/AITemplates";
import PromotionSettings from "@/promotions/pages/PromotionSettings";
import GoogleLoginSettings from "@/pages/GoogleLoginSettings";
import SecuritySettings from "@/pages/SecuritySettings";
import GoogleCallback from "@/pages/GoogleCallback";
import CreativeLayout from "@/creative/CreativeLayout";
import CreativeDashboard from "@/creative/pages/CreativeDashboard";
import Generate from "@/creative/pages/Generate";
import Edit from "@/creative/pages/Edit";
import Assets from "@/creative/pages/Assets";
import AssetDetail from "@/creative/pages/AssetDetail";
import BrandKits from "@/creative/pages/BrandKits";
import CreativeTemplates from "@/creative/pages/Templates";
import Jobs from "@/creative/pages/Jobs";
import Providers from "@/creative/pages/Providers";
import Publishing from "@/creative/pages/Publishing";
import Delivery from "@/creative/pages/Delivery";
import VideoStudio from "@/creative/pages/VideoStudio";
import VideoProject from "@/creative/pages/VideoProject";
import CreativeCampaigns from "@/creative/pages/Campaigns";
import CampaignPlan from "@/creative/pages/CampaignPlan";
import PublicContentPage from "@/pages/PublicContentPage";
import LegalCMS from "@/pages/LegalCMS";
import AdminOnly from "@/pages/admin/AdminOnly";
import AdminOrganizations from "@/pages/admin/AdminOrganizations";
import AdminOrganizationDetail from "@/pages/admin/AdminOrganizationDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminAI from "@/pages/admin/AdminAI";

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      {/* Public legal / content pages — NO authentication. */}
      <Route path="/privacy" element={<PublicContentPage slug="privacy" />} />
      <Route path="/terms" element={<PublicContentPage slug="terms" />} />
      <Route path="/cookies" element={<PublicContentPage slug="cookies" />} />
      <Route path="/data-deletion" element={<PublicContentPage slug="data-deletion" />} />
      <Route path="/contact" element={<PublicContentPage slug="contact" />} />
      <Route path="/support" element={<PublicContentPage slug="support" />} />
      <Route path="/legal/:slug" element={<PublicContentPage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/registry" element={<Registry />} />
        <Route path="/members" element={<Members />} />
        <Route path="/settings/api-keys" element={<ApiKeys />} />
        <Route path="/gdpr" element={<Gdpr />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/knowledge/:kbId" element={<Knowledge />} />
        <Route path="/knowledge/:kbId/items/:itemId" element={<KnowledgeItemEditor />} />
        <Route path="/events" element={<Events />} />
        <Route path="/governance" element={<Governance />} />
        <Route path="/media" element={<Media />} />
        <Route path="/bi" element={<BI />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/ai" element={<AIStudio />} />
        <Route path="/social" element={<Social />} />
        <Route path="/ecommerce" element={<Ecommerce />} />
        <Route path="/email" element={<EmailMarketing />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/product-hunter" element={<ProductHunter />} />
        <Route path="/settings/google-login" element={<GoogleLoginSettings />} />
        <Route path="/settings/security" element={<SecuritySettings />} />
        <Route path="/settings/legal" element={<LegalCMS />} />
        {/* Platform Administration Center — global, read-only, platform-admin only. */}
        <Route path="/admin/organizations" element={<AdminOnly><AdminOrganizations /></AdminOnly>} />
        <Route path="/admin/organizations/:orgId" element={<AdminOnly><AdminOrganizationDetail /></AdminOnly>} />
        <Route path="/admin/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
        <Route path="/admin/users/:userId" element={<AdminOnly><AdminUserDetail /></AdminOnly>} />
        <Route path="/admin/system" element={<AdminOnly><AdminSystem /></AdminOnly>} />
        <Route path="/admin/ai" element={<AdminOnly><AdminAI /></AdminOnly>} />
      </Route>
      <Route element={<RequireAuth />}>
        <Route path="/promotions" element={<PromotionLayout />}>
          <Route index element={<PromotionHome />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/:id" element={<CampaignDetail />} />
          <Route path="new" element={<NewCampaign />} />
          <Route path="catalog" element={<ProductCatalog />} />
          <Route path="preview" element={<PreviewCenter />} />
          <Route path="performance" element={<PerformanceAnalytics />} />
          <Route path="templates" element={<AITemplates />} />
          <Route path="settings" element={<PromotionSettings />} />
          <Route path="queue" element={<Queue />} />
          <Route path="timeline" element={<Timeline />} />
          <Route path="connections" element={<ConnectionHealthPage />} />
        </Route>
        <Route path="/creative" element={<CreativeLayout />}>
          <Route index element={<CreativeDashboard />} />
          <Route path="generate" element={<Generate />} />
          <Route path="video" element={<VideoStudio />} />
          <Route path="video/:projectId" element={<VideoProject />} />
          <Route path="campaigns" element={<CreativeCampaigns />} />
          <Route path="campaigns/:planId" element={<CampaignPlan />} />
          <Route path="edit" element={<Edit />} />
          <Route path="assets" element={<Assets />} />
          <Route path="assets/:assetId" element={<AssetDetail />} />
          <Route path="brand-kits" element={<BrandKits />} />
          <Route path="templates" element={<CreativeTemplates />} />
          <Route path="publishing" element={<Publishing />} />
          <Route path="delivery" element={<Delivery />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="providers" element={<Providers />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    applyStoredTheme();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrgProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </OrgProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
